import type { ReploAnnouncement } from "schemas/generated/announcement";

import * as React from "react";
import { useEffect, useState } from "react";

import { Button } from "@replo/design-system/components/button/Button";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import {
  PopoverContent,
  PopoverHeader,
  PopoverRoot,
  PopoverTitle,
  PopoverTrigger,
} from "@replo/design-system/components/popover/Popover";
import {
  SidebarMenuButton,
  useSidebar,
} from "@replo/design-system/components/sidebar/Sidebar";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import AnnouncementsMenuContent from "@v2/features/announcements/AnnouncementsMenuContent";
import { useListAnnouncements } from "@v2/features/announcements/hooks/useListAnnouncements";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import {
  ArrowUpRight,
  ChevronRight,
  LibraryBig,
  Megaphone,
} from "lucide-react";
import { coerceStringToBooleanSchema } from "schemas/utils";

type ResourceLink = {
  title: string;
  href?: string;
  startEnhancer?: React.ReactNode;
  endEnhancer?: React.ReactNode;
};

const MAX_ANNOUNCEMENT_HISTORY = 10;

const AnnouncementsMenu = ({
  source,
  items,
}: {
  source: "editor" | "dashboard";
  items?: ResourceLink[];
}) => {
  const isEditor = source === "editor";
  // Note (Patrick, 2025-11-03): Hide announcements during E2E tests
  const [isE2ETest] = useLocalStorageState("REPLO_IS_E2E_TEST", false, {
    schema: coerceStringToBooleanSchema,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [seenAnnouncementIds, setSeenAnnouncementIds] = useLocalStorageState<
    string[]
  >("replo.announcements.menu.seenAnnouncementIds", [], {
    validate: (value): value is string[] => {
      return (
        Array.isArray(value) && value.every((item) => typeof item === "string")
      );
    },
  });
  const logEvent = useLogAnalytics();
  const [selectedAnnouncement, setSelectedAnnouncement] =
    React.useState<ReploAnnouncement | null>(null);

  const { announcements, isLoading } = useListAnnouncements({
    limit: 4,
  });

  // Note (Ben O, 03-14-2025): We store the last seen announcement menu id in local storage
  // to determine if we should open the popover automatically on load.

  // Effect to update the seen announcements when the menu is open
  useEffect(() => {
    if (isOpen && !isLoading && announcements.length > 0) {
      let newSeenIds = [...seenAnnouncementIds];
      let hasNewIds = false;

      announcements.forEach((announcement) => {
        if (!newSeenIds.includes(announcement.id)) {
          newSeenIds = newSeenIds.filter((id) => id !== announcement.id);
          newSeenIds.push(announcement.id);
          hasNewIds = true;
        }
      });

      if (newSeenIds.length > MAX_ANNOUNCEMENT_HISTORY) {
        newSeenIds = newSeenIds.slice(-MAX_ANNOUNCEMENT_HISTORY);
        hasNewIds = true;
      }

      if (hasNewIds) {
        setSeenAnnouncementIds(newSeenIds);
      }
    }
  }, [
    isOpen,
    announcements,
    seenAnnouncementIds,
    setSeenAnnouncementIds,
    isLoading,
  ]);

  // Effect to automatically open the menu when there's a new announcement
  useEffect(() => {
    if (!isOpen && !isLoading && announcements.length > 0 && isEditor) {
      const hasNewAnnouncement = announcements.some(
        (announcement) => !seenAnnouncementIds.includes(announcement.id),
      );

      if (hasNewAnnouncement) {
        setIsOpen(true);
      }
    }
  }, [announcements, isLoading, seenAnnouncementIds, isOpen, isEditor]);

  // Effect to remove the selected announcement when the menu is closed
  useEffect(() => {
    if (!isOpen && selectedAnnouncement) {
      setSelectedAnnouncement(null);
    }
  }, [isOpen, selectedAnnouncement]);

  const handleOpen = () => {
    setIsOpen(true);
    logEvent("announcements.menu.opened", {});
  };

  const linkMenuItems = items ?? [];

  if (isE2ETest) {
    return null;
  }

  return (
    <PopoverRoot open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger render={<div className="w-full" />}>
        <div className="w-full">
          {source === "dashboard" ? (
            <DashboardAnnouncementsTrigger
              linkMenuItems={linkMenuItems}
              onWhatsNewClick={handleOpen}
              isPopoverOpen={isOpen}
            />
          ) : (
            <Tooltip content="Announcements" triggerAsChild>
              <Button
                icon
                variant="secondary"
                aria-label="Announcements"
                onClick={handleOpen}
              >
                <Megaphone />
              </Button>
            </Tooltip>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="end"
        sideOffset={16}
        showCloseButton={!selectedAnnouncement}
        UNSAFE_className={twMerge(
          "min-h-[375px] max-h-[375px] w-[300px] overflow-hidden",
          selectedAnnouncement ? "p-0" : "p-3",
        )}
      >
        {!selectedAnnouncement && (
          <PopoverHeader>
            <PopoverTitle>What&apos;s New</PopoverTitle>
          </PopoverHeader>
        )}
        <AnnouncementsMenuContent
          announcements={announcements}
          isLoading={isLoading}
          handleClose={() => setIsOpen(false)}
          selectedAnnouncement={selectedAnnouncement}
          setSelectedAnnouncement={setSelectedAnnouncement}
        />
      </PopoverContent>
    </PopoverRoot>
  );
};

/**
 * Dashboard-specific trigger that renders a SidebarMenuButton with a nested
 * menu for resource links and "What's New".
 */
const DashboardAnnouncementsTrigger = ({
  linkMenuItems,
  onWhatsNewClick,
  isPopoverOpen,
}: {
  linkMenuItems: ResourceLink[];
  onWhatsNewClick: () => void;
  isPopoverOpen: boolean;
}) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const { open, openMobile } = useSidebar();
  const isMobile = useIsMobile();
  const isSidebarExpanded = isMobile ? openMobile : open;

  return (
    <Menu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <MenuTrigger
        UNSAFE_className={twMerge(
          "w-full rounded-md",
          !isSidebarExpanded && "w-fit",
        )}
        render={
          <SidebarMenuButton
            tooltip="Learn More"
            UNSAFE_className={twMerge(
              "p-0 hover:bg-sheet",
              (isMenuOpen || isPopoverOpen) && "bg-button-tertiary-hover",
            )}
          >
            <div className="flex gap-2 justify-start items-center text-default p-1.5 cursor-pointer rounded-md w-full">
              <div className="flex h-4 w-4 items-center justify-center">
                <LibraryBig size={16} />
              </div>
              <span className="text-left text-sm truncate">Learn More</span>
              <ChevronRight size={16} className="shrink-0 ml-auto" />
            </div>
          </SidebarMenuButton>
        }
      />
      <MenuContent
        side="right"
        sideOffset={isSidebarExpanded ? 16 : 20}
        align="end"
        UNSAFE_className="w-[224px]"
      >
        <MenuItem
          label="What's New"
          startEnhancer={<Megaphone />}
          endEnhancer={<ArrowUpRight />}
          onClick={onWhatsNewClick}
        />
        {linkMenuItems.map((link) => (
          <MenuItem
            key={link.title}
            label={link.title}
            startEnhancer={link.startEnhancer}
            endEnhancer={link.endEnhancer}
            onClick={() => {
              window.open(link.href, "_blank", "noopener,noreferrer");
            }}
          />
        ))}
      </MenuContent>
    </Menu>
  );
};

export default AnnouncementsMenu;
